import { forwardRef, useImperativeHandle, useState, type ReactNode } from 'react'
import { Stethoscope, MessageSquare, FileText } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useKeyboardOpen } from '../../hooks/useKeyboardOpen'

export type MobilePane = 'patient' | 'chat' | 'note'

export interface MobileWorkspaceShellHandle {
  setActivePane: (pane: MobilePane) => void
}

interface MobileWorkspaceShellProps {
  patientPane: ReactNode
  chatPane: ReactNode
  notePane: ReactNode
}

// Active-tab accent matches the desktop collapsed rails: patient = clinical-cyan,
// chat = primary, note = purple-400 (WorkspaceManager.tsx note rail).
const TABS: ReadonlyArray<{ pane: MobilePane; label: string; Icon: typeof Stethoscope; activeClass: string }> = [
  { pane: 'patient', label: 'Patient', Icon: Stethoscope, activeClass: 'text-clinical-cyan' },
  { pane: 'chat', label: 'Chat', Icon: MessageSquare, activeClass: 'text-primary' },
  { pane: 'note', label: 'Note', Icon: FileText, activeClass: 'text-purple-400' },
]

/**
 * Phone layout for admission/flow workspaces: one full-screen pane at a time with
 * a bottom switcher. All panes stay mounted (hidden, not unmounted) to preserve
 * chat scroll + streaming state across switches.
 */
export const MobileWorkspaceShell = forwardRef<MobileWorkspaceShellHandle, MobileWorkspaceShellProps>(
  function MobileWorkspaceShell({ patientPane, chatPane, notePane }, ref) {
    const [activePane, setActivePane] = useState<MobilePane>('chat')
    useImperativeHandle(ref, () => ({ setActivePane }), [])

    // Reclaim the switcher's height for the focused pane while the keyboard is up.
    const keyboardOpen = useKeyboardOpen()

    return (
      <div className="flex flex-col h-full w-full min-w-0 bg-background">
        <div className="flex-1 min-h-0 relative">
          <div data-testid="pane-patient" className={cn('absolute inset-0 overflow-hidden', activePane !== 'patient' && 'hidden')}>
            {patientPane}
          </div>
          <div data-testid="pane-chat" className={cn('absolute inset-0 overflow-hidden', activePane !== 'chat' && 'hidden')}>
            {chatPane}
          </div>
          <div data-testid="pane-note" className={cn('absolute inset-0 overflow-hidden', activePane !== 'note' && 'hidden')}>
            {notePane}
          </div>
        </div>

        <nav
          role="tablist"
          aria-label="Workspace panes"
          className={cn(
            'flex-none flex border-t border-border/50 bg-surface',
            keyboardOpen && 'hidden',
          )}
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {TABS.map(({ pane, label, Icon, activeClass }) => {
            const isActive = activePane === pane
            return (
              <button
                key={pane}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={label}
                onClick={() => setActivePane(pane)}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] transition-colors',
                  isActive ? activeClass : 'text-muted-foreground',
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{label}</span>
              </button>
            )
          })}
        </nav>
      </div>
    )
  },
)
